/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { AdresseForm } from '@mairistem/adresses';
import { SectionHeader } from '@mairistem/administres-utils';

import { ESPACE_AVANT_SECTION } from './stylesSection';

interface ChampAdresseProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /**
   * Chemin de `data` portant l'adresse. Un chemin imbriqué est accepté
   * (`'declarant.adresseDomicile'`) : la lecture passe par `_.get`, l'écriture
   * par le `_.set` du provider d'assistant — comme `ChampLieu`.
   */
  name: string;
  /** Titre de la section (ex. « Adresse de domicile »). */
  title: string;
  /** Date de validité de la commune et de la voie (`'YYYY-MM-DD'`). */
  dateValidite?: string;
  /** Clé d'erreur à lire dans `errors` (défaut : `name`) — cf. `ChampLieu`. */
  cleErreur?: string;
  required?: boolean;
  disabled?: boolean;
}

/** Type d'adresse par défaut, et de loin le plus fréquent en base. */
const TYPE_PAR_DEFAUT = 'Commune';

/**
 * Saisie d'une adresse postale COMPLÈTE, calquée sur l'« Adresse de contact » du
 * recensement citoyen. À distinguer de `ChampLieu`, qui n'enveloppe qu'un LIEU
 * (commune / département / pays) : un domicile a une voie.
 *
 * Trois props font ce mode :
 *
 * - `saisie="libre"` — le mode assisté chercherait la voie au référentiel à la
 *   date de validité, or on numérise des actes dont la voie n'y figure plus ;
 * - `handleTypes` — ⚠️ **sans elle, les boutons radio ne sont pas rendus du
 *   tout** (la lib les conditionne à `handleTypes && manuel && showTypes`) ;
 * - `displayDepartement={false}` — comme la maquette.
 *
 * Le `title` va à `AdresseForm`, qui le place à gauche des boutons radio ; le
 * rendre au-dessus l'afficherait deux fois.
 *
 * ⚠️ Les DEUX rappels sont nécessaires : la lib notifie par `onChange` (champs
 * libres) et `onAdresseChange` (champs recherchables, changement de type).
 * Changer de type déclenche les deux dans le même tick, d'où le `ref` : deux
 * fusions calculées sur la même valeur de rendu s'écraseraient.
 */
const ChampAdresse = (props: ChampAdresseProps) => {
  const {
    data,
    errors = {},
    onChange,
    name,
    title,
    dateValidite,
    required = false,
    disabled = false,
    cleErreur = name,
  } = props;

  const adresseCourante = _.get(data, name);

  /** Dernière valeur émise, pour chaîner deux rappels d'un même tick. */
  const derniere = React.useRef(adresseCourante);

  React.useEffect(() => {
    derniere.current = adresseCourante;
  }, [adresseCourante]);

  const emettre = React.useCallback(
    (calcul: (precedente: Record<string, any>) => Record<string, any>) => {
      const suivante = calcul(derniere.current ?? {});

      derniere.current = suivante;
      onChange(null, { name, value: suivante });
    },
    [onChange, name],
  );

  /** Champs libres : un à la fois, sous la forme d'un évènement DOM. */
  const handleChampChange = React.useCallback(
    (evenement: { target?: { name?: string; value?: unknown } }) => {
      const cle = evenement?.target?.name;

      if (_.isEmpty(cle)) {
        return;
      }

      // La lib préfixe ses noms de `adresse.` ; on ne garde que le champ.
      const champ = (cle as string).replace(/^adresse\./, '');

      emettre((precedente) => ({
        ...precedente,
        [champ]: evenement?.target?.value,
      }));
    },
    [emettre],
  );

  /** Champs recherchables et changement de type : l'adresse entière. */
  const handleAdresseChange = React.useCallback(
    (payload: { adresse?: Record<string, any> }) => {
      if (_.isNil(payload?.adresse)) {
        return;
      }

      emettre(() => payload.adresse as Record<string, any>);
    },
    [emettre],
  );

  /**
   * `handleTypes` laisse à l'appelant le soin de fournir le type initial : la
   * lib ne le défaute que lorsqu'elle le gère elle-même. Sans ça, aucun bouton
   * radio n'apparaîtrait coché à l'ouverture.
   */
  const adresseAffichee = React.useMemo(
    () => ({
      ...adresseCourante,
      type: adresseCourante?.type ?? TYPE_PAR_DEFAUT,
    }),
    [adresseCourante],
  );

  return (
    /*
     * Le dégagement est porté par un CONTENEUR et non par le titre : celui-ci
     * est rendu par la lib à gauche de la ligne des boutons radio, une marge
     * haute dessus l'y désalignerait. Même valeur que les autres titres, pour
     * que ce bloc s'empile au même rythme.
     */
    <div style={{ marginTop: ESPACE_AVANT_SECTION }}>
      <AdresseForm
        // Rendu à gauche de la ligne des boutons radio par la lib elle-même.
        title={<SectionHeader title={title} required={required} />}
        // Déjà dans un `Form` : un second, imbriqué, serait invalide.
        noForm
        saisie="libre"
        handleTypes
        displayDepartement={false}
        adresse={adresseAffichee}
        dateValidite={dateValidite}
        error={errors?.[cleErreur] ?? null}
        disabled={disabled}
        onChange={handleChampChange}
        onItemChange={handleAdresseChange}
      />
    </div>
  );
};

export default ChampAdresse;
